import imMap, {
  imOpIsMap,
  imOpToObject
} from '@bumblehead/im'

import {
  gnpathParentDir as pathParentDir
} from './gnpath.js'

import {
  gnedgeIdCreatePart as edgeIdCreatePart,
  gnedgeMetaOptsSet as edgeMetaOptsSet,
  gnedgeMetaSenderGet as edgeMetaSenderGet,
  gnedgeMetaTargetGet as edgeMetaTargetGet,
  gnedgeMetaSenderKeyGet as edgeMetaSenderKeyGet,
  gnedgeMetaTargetKeyGet as edgeMetaTargetKeyGet,
  gnedgeCreate as edgeCreate
} from './gnedge.js'

import {
  gnnodeKeyFormat as nodeKeyFormat,
  gnnodeChildKeyCreate as nodeChildKeyCreate,
  gnnodeChildKeysGet as nodeChildKeysGet,
  gnnodeChildKeysDOMGet as nodeChildKeysDOMGet,
  gnnodeCreateFromJS as nodeCreateFromJS,
  gnnodeNSSendersGet as nodeNSSendersGet,
  gnnodeNSTargetsGet as nodeNSTargetsGet,
  gnnodeNSSenderSet as nodeNSSenderSet,
  gnnodeNSTargetSet as nodeNSTargetSet,
  gnnodeNSTargetRmTargetKey as nodeNSTargetRmTargetKey,
  gnnodeNSSenderRmSenderKey as nodeNSSenderRmSenderKey,
  gnnodeNSSenderGetByTargetProp as nodeNSSenderGetByTargetProp,
  gnnodeNSTargetGetBySenderProp as nodeNSTargetGetBySenderProp,
  gnnodeNSTargetGetByIdPartSender as nodeNSTargetGetByIdPartSender,
  gnnodeNSSenderGetByIdPartTarget as nodeNSSenderGetByIdPartTarget
} from './gnnode.js'

import {
  gnenumKEY as KEY,
  gnenumNAME as NAME,
  gnenumCHILDKEYS as CHILDKEYS,
  gnenumCHILDKEYSDOM as CHILDKEYSDOM,
  gnenumNSSENDERS as NSSENDERS,
  gnenumEDGEINDISABLE as EDGEINDISABLE
} from './gnenum.js'

import {
  gnerrNodeNotFound as errNodeNotFound,
  gnerrEdgeNodeKeyNotFound as errEdgeNodeKeyNotFound,
  gnerr_rootnodenotfound as errNodeRootNotFound
} from './gnerr.js'

import {
  gnutilNumOrStrIs as utilNumOrStrIs
} from './gnutil.js'

const gngraphCreate = () => imMap({})

const gngraphIs = graph =>
  imOpIsMap(graph) && graph.has('/')

const gngraphNodeRootGet = graph => {
  const noderoot = graph.get('/')
  if (!noderoot)
    throw errNodeRootNotFound(graph)

  return noderoot
}

// get node from js/json
const gngraphCreateFromJS = js => (
  js && Object.keys(js).reduce((gr, key) => (
    gr.set(js[key].key,
      nodeCreateFromJS(js[key]))), gngraphCreate()))

// set cnode as child on pnode, w/ given name
const gngraphNodeChildSet = (gr, pnode, cnode, name) => {
  const cname = nodeKeyFormat(name || cnode.get(NAME))
  const ckey = nodeChildKeyCreate(pnode, cname)
  const cnodefin = cnode.set(KEY, ckey)
  const pnodefin = pnode.set(
    CHILDKEYS, nodeChildKeysGet(pnode).concat(ckey))
  const graphfin = gr.set(ckey, cnodefin)

  if (!gr.has(pnode.get(KEY)))
    throw errNodeNotFound(gr, pnode, pnode.get(KEY))

  return [
    graphfin.set(pnodefin.get(KEY), pnodefin),
    pnodefin,
    cnodefin
  ]
}

const gngraphNodeSet = (gr, node) => (
  [gr.set(node.get(KEY), node), node])

// return first node with key pattern matching a root pattern
//
//   /rootnode
//   /not/root/node
// const gngraphNodeRootGet = (gr, rootre = /^[^/]*\/[^/]*$/) => (
//   gr.find((val, key) => rootre.test(key)) || null)

// tuple: [node, ns, prop, meta?]
const gngraphNodeEdgeSet = (gr, sendertuple, targettuple, opt = {}) => {
  const ndsender = sendertuple[0]
  const ndtarget = targettuple[0]
  const ndsenderkey = ndsender.get(KEY)
  const ndtargetkey = ndtarget.get(KEY)
  const spec = opt.spec

  sendertuple = [ndsenderkey].concat(sendertuple.slice(1))
  targettuple = [ndtargetkey].concat(targettuple.slice(1))

  // edge and edgesans; edgesans do not include "spec" details
  // used for resolving data from a target node. The data is
  // not needed, with maybe one exception: values that are
  // derived from a target "composite" value.
  //
  // ex,
  // ```
  // { key: '/rotation', name: 'rotation', subj: { xyz: [45, 45, 0] } }
  // { key: '/sliderx',
  //   name: 'sliderx',
  //   subj: { value: nsref('[/rotation].xyz').nth(0) } }
  // { key: '/slidery',
  //   name: 'slidery',
  //   subj: { value: nsref('[/rotation].xyz').nth(1) } }
  // ```
  //
  // "slidery" value above needs to be merged back into "rotations" xyz
  // so index position "COMPOSITEIDX" is stored on the edge. in the future
  // when there is time, this should probably be pre-processed at compile
  // time and stored directly on node, eg.
  // ```
  // { key: '/slidery',
  //   name: 'slidery',
  //   subj: { value: nsref('[/rotation].xyz').nth(1) },
  //   cache: { composite: ['value', 1] } }
  // ```
  let edge = edgeCreate(sendertuple, targettuple),
      edgesans = edgeCreate(sendertuple, targettuple.slice(0, 3))

  if (typeof opt.COMPOSITEIDX === 'number')
    edgesans = edgeMetaOptsSet(edgesans, { COMPOSITEIDX: opt.COMPOSITEIDX })

  if (spec)
    edge = edgeMetaOptsSet(edge, { spec })

  if (gr.has(ndtargetkey) && gr.has(ndsenderkey)) {
    gr = gr
      .set(ndtargetkey, ndtarget)
      .set(ndsenderkey, ndsender)
    gr = (spec && spec[EDGEINDISABLE]) ? gr : gr
      .set(ndtargetkey, nodeNSSenderSet(ndtarget, edgesans))
    gr = (spec && spec[EDGEINDISABLE]) ? gr : gr
      .set(ndsenderkey, nodeNSTargetSet(gr.get(ndsenderkey), edge))
  } else {
    console.error(imOpToObject(gr))
    throw errEdgeNodeKeyNotFound(gr,
      gr.has(ndtargetkey) ? ndsenderkey : ndtargetkey)
  }

  return [
    gr,
    gr.get(ndsenderkey),
    gr.get(ndtargetkey)
  ]
}

// redefines incoming and outgoing edge nodes to link directly to one another
// in place of 'removed' node.
//
// before
// ```
// ndsender -> node -> ndtarget
// ```
// after
// ```
// ndsender -> ndtarget
// ```
// const gngraphNodeEdgeRm = (gr, ndrm, targetprop) => {
const gngraphNodeEdgeRm = (gr, rmtuple) => {
  let ndrm = rmtuple[0],
      edgein = nodeNSSenderGetByTargetProp(ndrm, rmtuple[1], rmtuple[2]),
      edgeinkey = edgein && edgeMetaSenderKeyGet(edgein),
      nodesender,
      edgeout = nodeNSTargetGetBySenderProp(ndrm, rmtuple[1], rmtuple[2]),
      edgeoutkey = edgeout && edgeMetaTargetKeyGet(edgeout),
      nodetarget,
      nodermkey

  if (gr.has(edgeinkey)) {
    nodermkey = ndrm.get(KEY)
    nodesender = nodeNSTargetRmTargetKey(gr.get(edgeinkey), nodermkey)
    ndrm = nodeNSSenderRmSenderKey(ndrm, edgeinkey)
    gr = gr
      .set(edgeinkey, nodesender)
      .set(nodermkey, ndrm)
  }

  if (gr.has(edgeoutkey)) {
    nodermkey = ndrm.get(KEY)
    nodetarget = nodeNSSenderRmSenderKey(gr.get(edgeoutkey), nodermkey)
    ndrm = nodeNSTargetRmTargetKey(ndrm, edgeoutkey)
    gr = gr
      .set(edgeoutkey, nodetarget)
      .set(nodermkey, ndrm)
  }

  // fill in new edges connecting target to sender
  if (nodetarget && nodesender) {
    const edgeoutMetaSender = edgeMetaSenderGet(edgeout)
    const edgeoutMetaTarget = edgeMetaTargetGet(edgeout)
    gr = gngraphNodeEdgeSet(
      gr,
      [nodesender, edgeoutMetaSender[1], edgeoutMetaSender[2]],
      [nodetarget, edgeoutMetaTarget[1], edgeoutMetaTarget[2]])[0]
  }

  return [gr, ndrm]
}

// 'insert' the node within the edge relationship
//
// original edge from 'edgenode':
//   **edgenode** --targetprop--> ndtarget
//
// modified edge from 'edgenode':
//   **edgenode** --targetprop--> node --targetprop--> ndtarget
//
const gngraphNodeEdgeInSet = (gr, sendertuple, inserttuple) => {
  let ndsender = sendertuple[0],
      ndtarget,
      ndinsert = inserttuple[0],
      inedge,
      res

  const ndsenderkey = ndsender.get(KEY)
  const senderns = sendertuple[1]
  const senderprop = sendertuple[2]

  const insertns = inserttuple[1]
  const insertprop = inserttuple[2]

  const ndtargetedge = nodeNSTargetGetByIdPartSender(
    ndsender, edgeIdCreatePart(
      [ndsenderkey].concat(sendertuple.slice(1))))
  const ndtargetedgemetatarget = edgeMetaTargetGet(ndtargetedge)
  const ndtargetkey = ndtargetedgemetatarget[0]

  ndtarget = gr.get(ndtargetkey)
  inedge = nodeNSSenderGetByIdPartTarget(
    gr.get(ndtargetkey), edgeIdCreatePart(ndtargetedgemetatarget))

  ndsender = nodeNSTargetRmTargetKey(ndsender, ndtargetkey)
  ndtarget = nodeNSSenderRmSenderKey(ndtarget, edgeMetaSenderKeyGet(inedge))

  gr = gr
    .set(ndsender.get(KEY), ndsender)
    .set(ndtarget.get(KEY), ndtarget)

  res = gngraphNodeEdgeSet(gr,
    [ndsender, senderns, senderprop],
    [ndinsert, insertns, insertprop])
  gr = res[0]
  ndsender = res[1]
  ndinsert = res[2]

  res = gngraphNodeEdgeSet(gr,
    [ndinsert, insertns, insertprop],
    [ndtarget].concat(ndtargetedgemetatarget.slice(1)))
  gr = res[0]

  return [gr, ndsender]
}

// 'insert' the node within the edge relationship
//
// original edge from 'edgenode':
//   ndsender --targetprop--> **edgenode**
//
// modified edge from 'edgenode':
//   ndsender --targetprop--> node --targetprop-->  **edgenode**
//
const gngraphNodeEdgeOutSet = (gr, inserttuple, targettuple) => {
  let ndsender,
      ndtarget = targettuple[0],
      ndinsert = inserttuple[0],
      outedge,
      res

  const ndtargetkey = ndtarget.get(KEY)
  const targetns = targettuple[1]
  const targetprop = targettuple[2]

  const insertns = inserttuple[1]
  const insertprop = inserttuple[2]

  const ndsenderedge = nodeNSSenderGetByIdPartTarget(
    ndtarget, edgeIdCreatePart(
      [ndtargetkey].concat(targettuple.slice(1))))
  const ndsenderedgemetasender = edgeMetaSenderGet(ndsenderedge)
  const ndsenderkey = ndsenderedgemetasender[0]

  ndsender = gr.get(ndsenderkey)
  outedge = nodeNSTargetGetByIdPartSender(
    gr.get(ndsenderkey), edgeIdCreatePart(ndsenderedgemetasender))

  ndtarget = nodeNSSenderRmSenderKey(ndtarget, ndsenderkey)
  ndsender = nodeNSTargetRmTargetKey(ndsender, edgeMetaTargetKeyGet(outedge))

  gr = gr
    .set(ndtarget.get(KEY), ndtarget)
    .set(ndsender.get(KEY), ndsender)

  res = gngraphNodeEdgeSet(gr,
    [ndsender].concat(ndsenderedgemetasender.slice(1)),
    [ndinsert, insertns, insertprop])
  gr = res[0]
  ndsender = res[1]
  ndinsert = res[2]

  res = gngraphNodeEdgeSet(gr,
    [ndinsert, insertns, insertprop],
    [ndtarget, targetns, targetprop])
  gr = res[0]

  return [gr, ndsender]
}

const gngraphNodeEdgesWalkList = async (gr, nd, senderprop, edges, fn) => {
  const edge = edges[0]

  if (!edge)
    return [gr, gr.get(nd.get(KEY))]

  // `edge.meta[1][2]` is target node prop
  if (!senderprop || edgeMetaSenderGet(edge)[2] === senderprop)
    gr = await fn(gr, nd, edge)

  return gngraphNodeEdgesWalkList(gr, nd, senderprop, edges.slice(1), fn)
}

const gngraphNodeEdgesWalk = async (gr, ndsrc, senderprop, fn) => {
  const outedgearr = nodeNSTargetsGet(ndsrc)

  senderprop = utilNumOrStrIs(senderprop) && senderprop

  return gngraphNodeEdgesWalkList(gr, ndsrc, senderprop, outedgearr, fn)
}

const gngraphNodeEdgesRm = (gr, nd) => {
  const ndkey = nd.get(KEY)

  nd = nd.set(NSSENDERS, nodeNSSendersGet(nd).filter(edge => {
    const edgekey = edgeMetaSenderKeyGet(edge)

    if (gr.has(edgekey)) {
      ([gr] = gngraphNodeSet(gr, (
        nodeNSTargetRmTargetKey(gr.get(edgekey), ndkey))))
    }
  }))

  return [gr, nd]
}

const gngraphNodeParentGet = (gr, nd) => (
  gr.get(pathParentDir(nd.get(KEY))))

// remove,
//  * edges
//  * refs from parent
//  * key and def from graph
const gngraphNodeRm = (gr, nd) => {
  const nodekey = nd.get(KEY)
  const pnode = gngraphNodeParentGet(gr, nd)
  let res = gngraphNodeEdgesRm(gr, nd)
  gr = res[0]
  nd = res[1]
  if (pnode) {
    res = gngraphNodeSet(gr, pnode
      .set(CHILDKEYS, nodeChildKeysGet(pnode).filter(k => k !== nodekey))
      .set(CHILDKEYSDOM, nodeChildKeysDOMGet(pnode).filter(k => k !== nodekey)))
  }
  gr = res[0]

  return [gr.rm(nodekey), nd]
}

const gngraphNodeSiblingPrevGetKey = (gr, nd) => {
  const siblings = nodeChildKeysGet(gngraphNodeParentGet(gr, nd))
  const nodeindex = siblings.indexOf(nd.get(KEY))

  return nodeindex > 0
    ? siblings[nodeindex - 1] || null
    : null
}

const gngraphNodeSiblingNextGetKey = (gr, nd) => {
  const siblings = nodeChildKeysGet(gngraphNodeParentGet(gr, nd))
  const nodeindex = siblings.indexOf(nd.get(KEY))

  return nodeindex !== -1 && (nodeindex < siblings.length)
    ? siblings[nodeindex + 1] || null
    : null
}

const gngraphNodeDFSApply = (ss, cfg, gr, nd, applyfn) => {
  const childarr = nodeChildKeysGet(nd)

  gr = applyfn(gr, nd)

  return (function dfs (gr, nd, childarr, len, child, key) {
    if (!len--) return gr

    key = childarr[len]
    child = gr.get(key)

    // legacy error condition, occurred when multiple childs shared
    // same name and node was removed at first occurrence of name
    if (!child)
      throw errNodeNotFound(gr, nd, key)

    gr = gngraphNodeDFSApply(ss, cfg, gr, child, applyfn)

    return dfs(gr, nd, childarr, len)
  }(gr, nd, childarr, childarr.length))
}

const gngraphNodeDFSFind = (ss, cfg, gr, nd, findfn) => {
  const ndcarr = nodeChildKeysGet(nd)

  if (findfn(ss, cfg, gr, nd))
    return nd

  return (function dfs (gr, nd, ndcarr, len, ndc, key) {
    if (!len--) return null

    key = ndcarr[len]
    ndc = gr.get(key)

    if (!ndc) {
      // can occur when multiple childs share same name
      // and node is removed on first occurrence of name
      throw errNodeNotFound(gr, nd, key)
    }

    const node = gngraphNodeDFSFind(ss, cfg, gr, ndc, findfn)

    return node || dfs(gr, nd, ndcarr, len)
  }(gr, nd, ndcarr, ndcarr.length))
}

export {
  gngraphCreate,
  gngraphCreateFromJS,
  gngraphIs,
  gngraphNodeRootGet,
  gngraphNodeParentGet,
  gngraphNodeChildSet,
  gngraphNodeSet,
  gngraphNodeEdgeSet,
  gngraphNodeEdgeRm,
  gngraphNodeEdgeInSet,
  gngraphNodeEdgeOutSet,
  gngraphNodeEdgesWalkList,
  gngraphNodeEdgesWalk,
  gngraphNodeEdgesRm,
  gngraphNodeRm,
  gngraphNodeSiblingPrevGetKey,
  gngraphNodeSiblingNextGetKey,
  gngraphNodeDFSApply,
  gngraphNodeDFSFind
}

